"use client"

import { motion } from "framer-motion"
import Link from "next/link"
import { FaDocker, FaFigma, FaGithub } from "react-icons/fa6"
import {
    SiBiome,
    SiClaude,
    SiClerk,
    SiCypress,
    SiFirebase,
    SiGoogleanalytics,
    SiGooglecloud,
    SiGooglegemini,
    SiHttpie,
    SiJenkins,
    SiJira,
    SiLinear,
    SiNotion,
    SiPosthog,
    SiPostman,
    SiRaycast,
    SiResend,
    SiSupabase,
    SiVercel,
    SiWarp,
} from "react-icons/si"
import {
    CanvaIcon,
    CloudflareIcon,
    CodexIcon,
    ConvexIcon,
    CursorIcon,
    FirecrawlIcon,
    GhosttyIcon,
    JetBrainsIcon,
    ManusIcon,
    McpIcon,
    MicrosoftAzureIcon,
    OpenAIIcon,
    OpenCodeIcon,
    OpenRouterIcon,
    PlaywrightIcon,
    SentryIcon,
    TLDrawIcon,
    WorkOSIcon,
    ZedIcon,
} from "@/components/ui/tool-icons"
import { cn } from "@/lib/utils"

// Categorized tools
const toolCategories = [
    {
        title: "Development",
        className: "lg:col-span-2",
        tools: [
            {
                node: <CursorIcon className="size-5" />,
                title: "Cursor",
                href: "https://cursor.sh",
            },
            {
                node: <ZedIcon className="size-5" />,
                title: "Zed",
                href: "https://zed.dev",
            },
            {
                node: <SiWarp aria-hidden="true" />,
                title: "Warp",
                href: "https://www.warp.dev",
            },
            {
                node: <GhosttyIcon className="size-5" />,
                title: "Ghostty",
                href: "https://ghostty.org",
            },
            {
                node: <SiClaude aria-hidden="true" />,
                title: "Claude Code",
                href: "https://code.claude.com",
            },
            {
                node: <OpenCodeIcon className="size-5" />,
                title: "OpenCode",
                href: "https://opencode.ai",
            },
            {
                node: <CodexIcon className="size-5" />,
                title: "Codex",
                href: "https://developers.openai.com/codex",
            },
            {
                node: <OpenRouterIcon className="size-5" />,
                title: "OpenRouter",
                href: "https://openrouter.ai",
            },
            {
                node: <SiPosthog aria-hidden="true" />,
                title: "PostHog",
                href: "https://www.posthog.com",
            },
            {
                node: <SentryIcon className="size-5" />,
                title: "Sentry",
                href: "https://sentry.io",
            },
            {
                node: <FaDocker aria-hidden="true" />,
                title: "Docker",
                href: "https://docker.com",
            },
            {
                node: <SiGoogleanalytics aria-hidden="true" />,
                title: "Google Analytics",
                href: "https://analytics.google.com",
            },
            {
                node: <FaGithub aria-hidden="true" />,
                title: "GitHub",
                href: "https://github.com",
            },
            {
                node: <JetBrainsIcon className="size-5" />,
                title: "JetBrains",
                href: "https://www.jetbrains.com",
            },

            {
                node: <CloudflareIcon className="size-5" />,
                title: "Cloudflare",
                href: "https://cloudflare.com",
            },
            {
                node: <SiVercel aria-hidden="true" />,
                title: "Vercel",
                href: "https://vercel.com",
            },
            {
                node: <SiSupabase aria-hidden="true" />,
                title: "Supabase",
                href: "https://supabase.com",
            },
            {
                node: <WorkOSIcon className="size-5" />,
                title: "WorkOS",
                href: "https://workos.com",
            },
            {
                node: <SiClerk aria-hidden="true" />,
                title: "Clerk",
                href: "https://clerk.com",
            },
            {
                node: <SiJenkins aria-hidden="true" />,
                title: "Jenkins",
                href: "https://www.jenkins.io",
            },
            {
                node: <SiGooglecloud aria-hidden="true" />,
                title: "Google Cloud Platform",
                href: "https://cloud.google.com",
            },
            {
                node: <MicrosoftAzureIcon className="size-5" />,
                title: "Microsoft Azure",
                href: "https://azure.microsoft.com",
            },
            {
                node: <SiFirebase aria-hidden="true" />,
                title: "Firebase",
                href: "https://firebase.google.com",
            },
            {
                node: <SiResend aria-hidden="true" />,
                title: "Resend",
                href: "https://resend.com",
            },
            {
                node: <ConvexIcon className="size-5" />,
                title: "Convex",
                href: "https://www.convex.dev",
            },
            {
                node: <FirecrawlIcon className="size-5" />,
                title: "Firecrawl",
                href: "https://www.firecrawl.dev",
            },
            {
                node: <McpIcon className="size-5" />,
                title: "Model Context Protocol",
                href: "https://modelcontextprotocol.io",
            },
        ],
    },
    {
        title: "Design",
        className: "lg:col-span-1",
        tools: [
            {
                node: <FaFigma aria-hidden="true" />,
                title: "Figma",
                href: "https://figma.com",
            },
            {
                node: <CanvaIcon className="size-5" />,
                title: "Canva",
                href: "https://www.canva.com",
            },
            {
                node: <SiGooglegemini aria-hidden="true" />,
                title: "Google Labs Experiments",
                href: "https://labs.google/experiments",
            },
            {
                node: <SiGooglecloud aria-hidden="true" />,
                title: "Google Stitch",
                href: "https://stitch.google.com",
            },
            {
                node: <SiGooglegemini aria-hidden="true" />,
                title: "Google AI Studio",
                href: "https://aistudio.google.com",
            },
        ],
    },
    {
        title: "Productivity",
        className: "lg:col-span-1",
        tools: [
            {
                node: <SiNotion aria-hidden="true" />,
                title: "Notion",
                href: "https://notion.so",
            },
            {
                node: <TLDrawIcon className="size-5" />,
                title: "TLDraw",
                href: "https://www.tldraw.com",
            },
            {
                node: <SiLinear aria-hidden="true" />,
                title: "Linear",
                href: "https://linear.app",
            },
            {
                node: <SiJira aria-hidden="true" />,
                title: "Jira",
                href: "https://www.jira.com",
            },
            {
                node: <SiRaycast aria-hidden="true" />,
                title: "Raycast",
                href: "https://www.raycast.com",
            },
            {
                node: <OpenAIIcon className="size-5" />,
                title: "OpenAI",
                href: "https://www.openai.com",
            },
            {
                node: <SiClaude aria-hidden="true" />,
                title: "Claude",
                href: "https://www.anthropic.com",
            },
            {
                node: <ManusIcon className="size-5" />,
                title: "Manus AI",
                href: "https://www.manus.im",
            },
        ],
    },
    {
        title: "Testing",
        className: "lg:col-span-1",
        tools: [
            {
                node: <SiPostman aria-hidden="true" />,
                title: "Postman",
                href: "https://postman.com",
            },
            {
                node: <SiHttpie aria-hidden="true" />,
                title: "Httpie",
                href: "https://httpie.io",
            },
            {
                node: <SiCypress aria-hidden="true" />,
                title: "Cypress",
                href: "https://cypress.io",
            },
            {
                node: <PlaywrightIcon className="size-5" />,
                title: "Playwright",
                href: "https://playwright.dev",
            },
            {
                node: <SiBiome aria-hidden="true" />,
                title: "Biome",
                href: "https://biomejs.dev",
            },
            {
                node: <SiVercel aria-hidden="true" />,
                title: "Agent Browser",
                href: "https://agent-browser.dev",
            },
        ],
    },
]

export default function ToolsSectionMinimal() {
    return (
        <section className="container mx-auto px-4 py-20">
            <div className="border-border/70 border-t pt-12 dark:border-border/40">
                <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
                    {/* Section Header */}
                    <div className="lg:col-span-3">
                        <span className="font-mono text-muted-foreground text-xs uppercase tracking-widest">
                            Workflow
                        </span>
                    </div>

                    {/* Content */}
                    <div className="flex flex-col gap-12 lg:col-span-9">
                        <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
                            <motion.p
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5 }}
                                viewport={{ once: true }}
                                className="max-w-2xl font-light text-muted-foreground text-xl leading-relaxed md:text-3xl"
                            >
                                The software I open{" "}
                                <span className="font-normal text-foreground">
                                    every day
                                </span>
                                .
                            </motion.p>
                            <Link
                                href="/resources"
                                className="group inline-flex items-center gap-1.5 font-mono text-muted-foreground text-xs uppercase tracking-widest transition-colors hover:text-foreground"
                            >
                                <span>Browse directory</span>
                                <span className="transition-transform duration-200 group-hover:translate-x-1">
                                    →
                                </span>
                            </Link>
                        </div>

                        <div className="grid grid-cols-1 gap-px border border-border/70 bg-border/70 md:grid-cols-2 lg:grid-cols-3 dark:border-border/40 dark:bg-border/40">
                            {toolCategories.map((category, categoryIndex) => (
                                <motion.div
                                    key={category.title}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{
                                        delay: categoryIndex * 0.1,
                                        duration: 0.5,
                                    }}
                                    viewport={{ once: true }}
                                    className={cn(
                                        "flex flex-col gap-6 bg-background p-8",
                                        category.className
                                    )}
                                >
                                    <h3 className="font-mono text-muted-foreground text-xs uppercase tracking-widest">
                                        {category.title}
                                    </h3>
                                    <div className="flex flex-wrap gap-2">
                                        {category.tools.map(
                                            (tool, toolIndex) => (
                                                <motion.a
                                                    key={tool.title}
                                                    href={tool.href}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    initial={{
                                                        opacity: 0,
                                                        scale: 0.9,
                                                    }}
                                                    whileInView={{
                                                        opacity: 1,
                                                        scale: 1,
                                                    }}
                                                    transition={{
                                                        delay:
                                                            categoryIndex *
                                                                0.1 +
                                                            toolIndex * 0.03,
                                                        duration: 0.2,
                                                    }}
                                                    viewport={{ once: true }}
                                                    className="group flex items-center gap-2 border border-border/70 bg-muted/5 px-3 py-1.5 transition-colors hover:bg-muted/50 dark:border-border/40"
                                                >
                                                    <span className="text-muted-foreground transition-colors duration-300 group-hover:text-foreground">
                                                        {tool.node}
                                                    </span>
                                                    <span className="font-mono text-muted-foreground text-xs uppercase tracking-widest transition-colors group-hover:text-foreground">
                                                        {tool.title}
                                                    </span>
                                                </motion.a>
                                            )
                                        )}
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}
